import { useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Booking } from '../../lib/types'
import { formatPrice, timeAgo } from '../../lib/format'
import { LinearStatusBadge } from './StatusBadges'
import { RecordRebookDialog } from './RecordRebookDialog'
import { Button } from '@perch/ui'

export function PriceDropTableRow({ booking, onClick, onRebooked, session }: {
  booking: Booking
  onClick?: () => void
  onRebooked?: (bookingId: string) => void
  session: Session
}) {
  const [showRebook, setShowRebook] = useState(false)
  const segments = booking.segments || []
  const firstSeg = segments[0]
  const lastSeg = segments[segments.length - 1]
  const route = firstSeg && lastSeg ? `${firstSeg.origin} → ${lastSeg.destination}` : 'N/A'

  const handlePnrClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(booking.pnr).catch(() => {})
    // Brief visual feedback — the PNR briefly changes style
    const target = e.currentTarget as HTMLElement
    target.classList.add('text-emerald-600')
    setTimeout(() => target.classList.remove('text-emerald-600'), 1000)
  }

  return (
    <>
      <tr className="hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 cursor-pointer" onClick={onClick}>
        <td className="font-medium text-stone-900 dark:text-stone-100">{booking.user_name}</td>
        <td className="text-stone-600 dark:text-stone-400 font-medium">{booking.airline_code}</td>
        <td>
          <span className="airport-code text-stone-900 dark:text-stone-100">{route}</span>
          {segments.length > 1 && <span className="text-stone-400 text-xs ml-1">({segments.length})</span>}
        </td>
        <td>
          <button
            onClick={handlePnrClick}
            className="font-mono text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors cursor-copy"
            title="Click to copy PNR"
            aria-label="Copy PNR to clipboard"
          >
            {booking.pnr}
          </button>
        </td>
        <td className="text-stone-700 dark:text-stone-300 font-data tabular-nums">
          {formatPrice(booking.is_award_booking ? booking.points_used : booking.total_price_paid, booking.is_award_booking)}
        </td>
        <td className="text-stone-700 dark:text-stone-300 font-data tabular-nums">
          {formatPrice(booking.latest_price, booking.is_award_booking)}
        </td>
        <td>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-xs font-data font-semibold tabular-nums">
            {formatPrice(booking.potential_savings, booking.is_award_booking)}
          </span>
        </td>
        <td>
          {booking.linear_issue ? (
            <LinearStatusBadge issue={booking.linear_issue} />
          ) : (
            <span className="text-stone-300 dark:text-stone-600 text-xs">—</span>
          )}
        </td>
        <td className="text-stone-500 text-xs">{timeAgo(booking.last_checked_at)}</td>
        <td>
          <Button
            onClick={(e) => { e.stopPropagation(); setShowRebook(true) }}
            size="sm"
            variant="emerald"
            className="whitespace-nowrap"
          >
            Record Rebook
          </Button>
        </td>
      </tr>

      {showRebook && (
        <RecordRebookDialog
          booking={booking}
          session={session}
          onClose={() => setShowRebook(false)}
          onRebooked={(bookingId) => {
            setShowRebook(false)
            onRebooked?.(bookingId)
          }}
        />
      )}
    </>
  )
}
